import React, {useState} from "react";
import Modal from "./Modal";
import styles, {C} from "../styles";
import {slugify, isoToParisLocal, parisLocalToIso} from "../utils";
import {apiAdminCreateCampaign, apiAdminUpdateCampaign} from "../api/adminCampaigns";

function Field({id, label, error, hint, children}) {
    return (
        <div style={{marginBottom: 12}}>
            <label htmlFor={id} style={{display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4}}>{label}</label>
            {children}
            {hint && <div style={{fontSize: 12, color: C.textMuted, marginTop: 2}}>{hint}</div>}
            {error && <div role="alert" style={{fontSize: 12, color: C.danger, marginTop: 2}}>{error}</div>}
        </div>
    );
}

const inputStyle = {...styles.input, marginBottom: 0};

export default function CampaignFormModal({campaign, onClose, onSaved}) {
    const isEdit = Boolean(campaign);
    const [name, setName] = useState(campaign?.name || "");
    const [slug, setSlug] = useState(campaign?.slug || "");
    const [slugTouched, setSlugTouched] = useState(false);
    const [file, setFile] = useState(null);
    const [starts, setStarts] = useState(campaign ? isoToParisLocal(campaign.starts_at) : "");
    const [ends, setEnds] = useState(campaign ? isoToParisLocal(campaign.ends_at) : "");
    const [isMain, setIsMain] = useState(Boolean(campaign?.is_main));
    // Isolée par défaut : il faut un geste volontaire pour retirer cette protection.
    const [isolated, setIsolated] = useState(campaign ? campaign.isolated !== false : true);
    const [fieldErrors, setFieldErrors] = useState({});
    const [globalError, setGlobalError] = useState("");
    const [saving, setSaving] = useState(false);

    function changeName(value) {
        setName(value);
        // L'adresse suit le nom tant que l'admin ne l'a pas retouchée à la main.
        if (!isEdit && !slugTouched) setSlug(slugify(value));
    }

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        setFieldErrors({});
        setGlobalError("");
        const startsAt = parisLocalToIso(starts);
        const endsAt = parisLocalToIso(ends);
        let result;
        if (isEdit && !file) {
            result = await apiAdminUpdateCampaign(campaign.id, {name, starts_at: startsAt, ends_at: endsAt, is_main: isMain, isolated});
        } else if (isEdit) {
            // Champs et nouveau fichier dans une seule requête : un fichier refusé n'enregistre pas le reste.
            const form = new FormData();
            form.append("name", name);
            form.append("starts_at", startsAt);
            form.append("ends_at", endsAt);
            form.append("is_main", String(isMain));
            form.append("isolated", String(isolated));
            form.append("file", file);
            result = await apiAdminUpdateCampaign(campaign.id, form);
        } else {
            const form = new FormData();
            form.append("name", name);
            form.append("slug", slug);
            form.append("starts_at", startsAt);
            form.append("ends_at", endsAt);
            form.append("is_main", String(isMain));
            form.append("isolated", String(isolated));
            if (file) form.append("file", file);
            result = await apiAdminCreateCampaign(form);
        }
        setSaving(false);
        if (result?.error) {
            setFieldErrors(result.fields || {});
            setGlobalError(result.fields ? "" : (result.message || result.error));
            return;
        }
        onSaved();
    }

    const publicBase = `${window.location.origin}/campagne/`;

    return (
        <Modal title={isEdit ? "Modifier la campagne" : "Nouvelle campagne"} onClose={onClose} scrollable>
            <form onSubmit={submit} noValidate>
                <Field id="campaign-name" label="Nom de la campagne" error={fieldErrors.name} hint="Nom interne, visible seulement ici.">
                    <input id="campaign-name" value={name} onChange={e => changeName(e.target.value)} style={inputStyle}/>
                </Field>
                <Field
                    id="campaign-slug" label="Adresse" error={fieldErrors.slug}
                    hint={isEdit
                        ? "L'adresse ne peut plus être modifiée : le changer casserait les liens déjà envoyés."
                        : `${publicBase}${slug || "…"}/ — minuscules, chiffres et tirets ; elle ne pourra plus changer après la création.`}
                >
                    <input
                        id="campaign-slug" value={slug} readOnly={isEdit}
                        onChange={e => { setSlug(e.target.value); setSlugTouched(true); }}
                        style={{...inputStyle, ...(isEdit ? {backgroundColor: "#EEE", color: C.textMuted} : {})}}
                    />
                </Field>
                <Field
                    id="campaign-file" label="Fichier (.html ou .zip)" error={fieldErrors.file}
                    hint={isEdit ? "Facultatif : laissez vide pour garder le fichier actuel." : "Texte uniquement. Un zip doit contenir un index.html ; images et polices à intégrer en base64."}
                >
                    <input id="campaign-file" type="file" accept=".html,.zip" onChange={e => setFile(e.target.files[0] || null)}/>
                </Field>
                <Field id="campaign-start" label="Début (heure de Paris)" error={fieldErrors.starts_at}>
                    <input id="campaign-start" type="datetime-local" value={starts} onChange={e => setStarts(e.target.value)} style={inputStyle}/>
                </Field>
                <Field id="campaign-end" label="Fin (heure de Paris)" error={fieldErrors.ends_at}>
                    <input id="campaign-end" type="datetime-local" value={ends} onChange={e => setEnds(e.target.value)} style={inputStyle}/>
                </Field>
                <div style={{marginBottom: 16}}>
                    <label style={{display: "flex", alignItems: "center", gap: 8, fontSize: 14}}>
                        <input type="checkbox" checked={isMain} onChange={e => setIsMain(e.target.checked)}/>
                        Campagne principale
                    </label>
                    <div style={{fontSize: 12, color: C.textMuted, marginLeft: 24}}>
                        L'adresse {publicBase} y mène, et un lien apparaît sur l'accueil du site tant qu'elle est en ligne.
                    </div>
                </div>
                <div style={{marginBottom: 16}}>
                    <label style={{display: "flex", alignItems: "center", gap: 8, fontSize: 14}}>
                        <input type="checkbox" checked={isolated} onChange={e => setIsolated(e.target.checked)}/>
                        Isolation de sécurité (recommandé)
                    </label>
                    <div style={{fontSize: 12, color: C.textMuted, marginLeft: 24}}>
                        La page est servie dans un espace isolé : elle ne peut pas lire la session des agents. Décochez uniquement
                        si elle doit afficher un contenu externe qui refuse les pages isolées, comme une vidéo YouTube.
                    </div>
                    {!isolated && (
                        <div role="alert" style={{fontSize: 12, color: C.danger, marginLeft: 24, marginTop: 4}}>
                            Sans isolation, le code de cette page pourrait lire la session de toute personne connectée qui
                            l'ouvre. Ne déposez ici qu'un fichier dont vous maîtrisez entièrement le contenu.
                        </div>
                    )}
                </div>
                {globalError && <div role="alert" style={{color: C.danger, fontSize: 13, marginBottom: 12}}>{globalError}</div>}
                <div style={{display: "flex", justifyContent: "flex-end", gap: 8}}>
                    <button type="button" onClick={onClose} style={styles.button}>Annuler</button>
                    <button type="submit" disabled={saving} style={styles.primaryButton}>
                        {isEdit ? "Enregistrer" : "Créer la campagne"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
